import { AsyncPipe, DecimalPipe, UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { dateFormatPipe } from '@app/pipes/datepipe';
import { AttachmentService } from '@app/v2/services/travel-request/attachment.service';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import {
    Chip,
    MdsCardComponent,
    MdsCardContentComponent,
    MdsChipComponent,
} from '@medpacesoftwaredevelopment/designsystem';
import { DateFormat } from '@models/date-format';
import { Receipt, TravelRequestStatus } from '@models/request';
import { TravelRequestStateService } from '@services/state-management/travel-request-state.service';
import { map, shareReplay } from 'rxjs';
import { MerchantDetailsViewComponent } from '../../merchant-details/merchant-details-view/merchant-details-view.component';
import { LabeledTextComponent } from '../../labeled-text/labeled-text.component';

@Component({
    selector: 'medpace-receipt-view',
    imports: [
        dateFormatPipe,
        UpperCasePipe,
        DecimalPipe,
        AsyncPipe,
        MerchantDetailsViewComponent,
        MdsCardComponent,
        MdsCardContentComponent,
        LabeledTextComponent,
        MdsChipComponent,
    ],
    templateUrl: './receipt-view.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReceiptViewComponent {
    index = input.required<number>();
    DateFormat = DateFormat;
    travelRequestStateService = inject(TravelRequestStateService);
    private readonly userService = inject(UserServiceV2);
    isCrc = UserServiceV2.isCrc();
    readonly receiptTypeOptions = this.travelRequestStateService.receiptTypeOptions;
    model = input.required<Receipt>();
    receiptType = computed(() => {
        const receiptType = this.model().type;
        return this.receiptTypeOptions.find((option) => option.value === receiptType);
    });
    amountUsdChip = computed((): Chip[] | null => {
        const model = this.model();
        if (!model.currencyCode || model.currencyCode.toUpperCase() === 'USD') return null;
        if (model.amountUsd == null) return null;
        const value = `${model.amountUsd.toFixed(2)} USD · reporting`;
        return [{ value, ariaLabel: value, removable: false }];
    });

    attachmentService = inject(AttachmentService);
    elevatedAccess$ = this.userService.permissions.request.access.elevated$;
    isInProcessOrHigher$ = this.travelRequestStateService.getStaticState().pipe(
        map((request) => !!request && request.status?.statusId > TravelRequestStatus.Submitted),
        shareReplay({ bufferSize: 1, refCount: true })
    );
    downloadAttachment() {
        const model = this.model();
        if (!model) throw new Error('Receipt model is missing!');
        if (!model.attachmentInfoId) throw new Error('Missing attachment id!');

        const requestId = model.travelRequestId; // for now only supports travelRequst, attachmentService also only downloads from travelrequest
        if (!requestId) throw new Error('Travel request id missing!');

        if (!model.type) throw new Error('Receipt type missing!');
        const attachmentName = this.attachmentService.travelRequestAttachmentName;

        this.attachmentService.downloadAttachment(requestId, {
            attachmentId: model.attachmentInfoId,
            attachmentName: attachmentName,
        });
    }
}
